import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { supabase } from '../services/supabase';
import type { UserContext } from '../types/organization.types';
import type { OrganizationSubscription } from '../../domains/subscriptions/types';

const ACTIVE_ORG_KEY = 'videreo_active_org';

function getOrgSlugFromPath(pathname: string): string | null {
  const match = /^\/org\/([^/]+)/.exec(pathname);
  return match ? match[1] : null;
}

export interface BootstrapTargetOrg {
  organization: {
    id: string;
    name: string;
    slug: string;
    type: string;
    status: string;
    icon_url: string | null;
    homepage: string | null;
    settings: Record<string, unknown>;
    created_at: string;
  };
  subscription: OrganizationSubscription | null;
  task_count: number;
  role: string;
}

export interface AppBootstrapData {
  userContext: UserContext | null;
  targetOrg: BootstrapTargetOrg | null;
}

function buildUserContext(profile: {
  id: string;
  username: string | null;
  email: string | null;
  role: string;
  updated_username: boolean;
}, organizations: Array<{
  id: string;
  organization_id: string;
  profile_id: string;
  role: string;
  invited_by: string | null;
  invited_at: string;
  accepted_at: string | null;
  organization: {
    id: string;
    name: string;
    slug: string;
    type: string;
    status: string;
    icon_url: string | null;
    homepage: string | null;
    settings: Record<string, unknown>;
    created_at: string;
  };
}>): UserContext {
  return {
    profileId: profile.id,
    email: profile.email,
    username: profile.username,
    updatedUsername: profile.updated_username,
    systemRole: (profile.role as UserContext['systemRole']) || 'creator',
    organizations: organizations.map((m) => ({
      id: m.id,
      organization_id: m.organization_id,
      profile_id: m.profile_id,
      role: m.role as UserContext['organizations'][0]['role'],
      invited_by: m.invited_by,
      invited_at: m.invited_at,
      accepted_at: m.accepted_at,
      organization: m.organization as UserContext['organizations'][0]['organization'],
    })),
    isSystemAdmin: profile.role === 'admin',
    isOrgUser: profile.role === 'org',
  };
}

export function useAppBootstrap(): {
  data: AppBootstrapData | undefined;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<unknown>;
} {
  const location = useLocation();
  const orgSlug = getOrgSlugFromPath(location.pathname);
  const orgId = orgSlug ? null : (typeof window !== 'undefined' ? localStorage.getItem(ACTIVE_ORG_KEY) : null);

  const query = useQuery({
    queryKey: ['app-bootstrap', orgId, orgSlug],
    queryFn: async (): Promise<AppBootstrapData> => {
      try {
        const params: { p_org_id?: string; p_org_slug?: string } = {};
        if (orgId) params.p_org_id = orgId;
        if (orgSlug) params.p_org_slug = orgSlug;

        const { data, error } = await supabase.rpc('portal_bootstrap', params);

        if (error) {
          if (error.code === 'PGRST301' || error.message?.toLowerCase().includes('jwt')) {
            return { userContext: null, targetOrg: null };
          }
          throw new Error(error.message || 'Failed to fetch bootstrap');
        }

        const raw = data as {
          profile: { id: string; username: string | null; email: string | null; role: string; updated_username: boolean } | null;
          organizations: Array<{
            id: string;
            organization_id: string;
            profile_id: string;
            role: string;
            invited_by: string | null;
            invited_at: string;
            accepted_at: string | null;
            organization: {
              id: string;
              name: string;
              slug: string;
              type: string;
              status: string;
              icon_url: string | null;
              homepage: string | null;
              settings: Record<string, unknown>;
              created_at: string;
            };
          }>;
          target_org: BootstrapTargetOrg | null;
        };

        if (!raw?.profile) {
          return { userContext: null, targetOrg: null };
        }

        const userContext = buildUserContext(raw.profile, raw.organizations ?? []);
        return {
          userContext,
          targetOrg: raw.target_org ?? null,
        };
      } catch (e) {
        if (e instanceof Error && e.message === 'Not authenticated') {
          return { userContext: null, targetOrg: null };
        }
        throw e;
      }
    },
    staleTime: 60_000,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error as Error | null,
    refetch: query.refetch,
  };
}
